import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { Button } from './ui/button';

/**
 * CookieConsent Component
 * GDPR-compliant cookie consent banner
 */
const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState(() => {
    // Initialize from localStorage if available
    const consent = localStorage.getItem('cookie_consent');
    if (consent) {
      try {
        const saved = JSON.parse(consent);
        return saved.preferences || { necessary: true, analytics: false, marketing: false };
      } catch (e) {
        return { necessary: true, analytics: false, marketing: false };
      }
    }
    return { necessary: true, analytics: false, marketing: false };
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Delay showing the banner slightly
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (acceptAll = false) => {
    const consentData = {
      timestamp: new Date().toISOString(),
      preferences: acceptAll 
        ? { necessary: true, analytics: true, marketing: true }
        : preferences
    };
    
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    setIsVisible(false);
    
    // Dispatch event for analytics scripts to check
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
      detail: consentData.preferences 
    }));
  };

  const handleAcceptAll = () => {
    setPreferences({ necessary: true, analytics: true, marketing: true });
    saveConsent(true);
  };

  const handleRejectAll = () => {
    setPreferences({ necessary: true, analytics: false, marketing: false });
    saveConsent(false);
  };

  const handleSavePreferences = () => {
    saveConsent(false);
    setShowPreferences(false);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
      data-testid="cookie-consent"
    >
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {!showPreferences ? (
          /* Main Banner */
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-teal-50 rounded-full flex-shrink-0">
                <Cookie className="h-6 w-6 text-teal-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">
                  We value your privacy
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  We use cookies to enhance your browsing experience, analyze site traffic, 
                  and personalize content. By clicking &quot;Accept All&quot;, you consent to our use of cookies.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={handleAcceptAll}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="cookie-accept-all"
                  >
                    Accept All
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleRejectAll}
                    data-testid="cookie-reject-all"
                  >
                    Reject All
                  </Button>
                  <Button 
                    variant="ghost"
                    onClick={() => setShowPreferences(true)}
                    className="text-teal-600"
                    data-testid="cookie-preferences"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Manage Preferences
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Preferences Panel */
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-lg text-gray-900">Cookie Preferences</h3>
              <button 
                onClick={() => setShowPreferences(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              {/* Necessary Cookies */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Necessary Cookies</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Required for the website to function. Cannot be disabled.
                  </p>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={true} 
                    disabled 
                    className="h-5 w-5 rounded border-gray-300 text-teal-600"
                  />
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Analytics Cookies</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Help us understand how visitors interact with our website.
                  </p>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                    className="h-5 w-5 rounded border-gray-300 text-teal-600 cursor-pointer"
                    data-testid="cookie-analytics-toggle"
                  />
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Marketing Cookies</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Used to deliver personalized ads and track ad performance.
                  </p>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                    className="h-5 w-5 rounded border-gray-300 text-teal-600 cursor-pointer"
                    data-testid="cookie-marketing-toggle"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={handleSavePreferences}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="cookie-save-preferences"
              >
                Save Preferences
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowPreferences(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
